/**
 * CopyGroupButton — copies a single formula group to the system clipboard
 * and local history.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useState } from 'react';
import { Copy, Check } from 'lucide-react';
import type { FormulaGroup } from '@trafficpulse/shared';

import { Button } from '@/components/ui/layout/Button';
import { Tooltip } from '@/components/ui/Tooltip';

import { useFormulaClipboard } from '../useFormulaClipboard';

export interface CopyGroupButtonProps {
  group: FormulaGroup;
}

const CopyGroupButton: FC<CopyGroupButtonProps> = ({ group }) => {
  const { copyGroup } = useFormulaClipboard();
  const [copied, setCopied] = useState(false);

  const handleClick = async () => {
    await copyGroup(group);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Tooltip content="Copy this group (paste it into another campaign)" side="bottom">
      <Button
        type="button"
        size="icon"
        variant="ghost"
        onClick={handleClick}
        className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
        aria-label="Copy formula group"
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-success" aria-hidden />
        ) : (
          <Copy className="h-3.5 w-3.5" aria-hidden />
        )}
      </Button>
    </Tooltip>
  );
};

export default CopyGroupButton;
